import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/Dialog';
import { RadioGroup, RadioGroupItem } from '@/components/RadioGroup';
import Spinner from '@/components/Spinner';
import VisuallyHidden from '@/components/VisuallyHidden';
import { PLAN_TYPE } from '@/const/plans';
import { useSubscriptionInfo } from '@/hooks/user';
import Edit from '@/icons/edit.svg';
import { setPlansModalOpen, setPrice, setSelectedPlan } from '@/store/checkout';
import { cn, showError, showSuccess } from '@/utils';
import { formatPrice } from '@/utils/money';

import { getPlanCheckoutName } from '../utils';

function SelectPlanModal({ open, onOpenChange }) {
	const subscriptionInfo = useSubscriptionInfo();

	/*
		When reloading checkout there is no billing data, so we have to consume `canUseTrial` from profile data.
	*/

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>
				<Button variant="outline" size="iconMd">
					<Edit className="text-black" />
				</Button>
			</DialogTrigger>

			<DialogContent className="w-[320px] p-0 md:w-[606px]">
				<SelectPlanContent
					onOpenChange={onOpenChange}
					blockedOptions={
						subscriptionInfo.canUseTrial ? [] : [PLAN_TYPE.TRIAL]
					}
				/>
			</DialogContent>
		</Dialog>
	);
}

export function SelectPlanContent({
	onOpenChange,
	confirmButtonText = 'common.save',
	onChangePlan = false,
	blockedOptions = [],
	filterTrial = false
}) {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const { prices, selectedPlan, priceId } = useSelector(
		state => state.checkout
	);
	const {
		isTrial,
		plan: currentPlan,
		cancellationRequested
	} = useSubscriptionInfo();

	const currentPlanId = prices.find(p => p.name === currentPlan)?.handlerId;

	useEffect(() => {
		dispatch(setSelectedPlan(priceId || currentPlanId));
	}, []);

	const [changingPlan, setChangingPlan] = useState(false);

	const changePlan = async () => {
		try {
			setChangingPlan(true);
			// if the selected plan is the current one, we don't need to change the plan
			if (selectedPlan !== priceId) {
				dispatch(setPrice({ id: selectedPlan }));
			}
			dispatch(setPlansModalOpen(false));
			showSuccess(t('checkout.toast.changePlanSuccess'));
		} catch (error) {
			showError(error);
		} finally {
			setChangingPlan(false);
		}
	};

	const isBlockedOption = planName => {
		return (
			blockedOptions &&
			(blockedOptions[0] === 'all' || blockedOptions.includes(planName))
		);
	};

	const parsedPrices = filterTrial
		? prices.filter(item => item.name !== PLAN_TYPE.TRIAL)
		: prices;

	return (
		<>
			<DialogHeader className="flex h-12 flex-row items-center justify-between border-b border-grey-200 px-4">
				<DialogTitle
					className={cn(
						'font-title text-xs font-semibold leading-5 md:text-sm md:leading-6',
						'text-sm leading-6'
					)}
				>
					{t('checkout.selectPlan.title')}
				</DialogTitle>
				<VisuallyHidden asChild>
					<DialogDescription>
						{t('checkout.selectPlan.description')}
					</DialogDescription>
				</VisuallyHidden>
			</DialogHeader>

			<div className="relative flex min-h-[100px] flex-col gap-2 p-5">
				{!prices.length && (
					<Spinner className="absolute inset-0 m-auto" size={24} />
				)}
				<RadioGroup
					onValueChange={value => dispatch(setSelectedPlan(value))}
					value={selectedPlan}
				>
					{parsedPrices.map(plan => {
						const isSelected =
							(isTrial &&
								plan.name === 'trial' &&
								cancellationRequested) ||
							(!(isTrial && cancellationRequested) &&
								selectedPlan === plan.handlerId);

						return (
							<div
								key={plan.name}
								className={cn(
									'mb-1 flex w-full cursor-pointer items-center justify-between rounded border px-4 py-5 text-sm transition-colors focus:border-black focus-visible:outline-none',
									isSelected
										? 'border-red bg-red-background'
										: 'border-grey-300 hover:bg-grey-100',
									isBlockedOption(plan.name) && 'cursor-not-allowed'
								)}
								onClick={() =>
									!isBlockedOption(plan.name) &&
									dispatch(setSelectedPlan(plan.handlerId))
								}
							>
								<div className="flex w-full items-center gap-3">
									<RadioGroupItem
										value={plan.handlerId}
										id={plan.handlerId}
										checked={isSelected}
										disabled={isBlockedOption(plan.name)}
									/>
									<div className="flex w-full flex-col justify-between lg:flex-row">
										<div className="flex flex-col">
											<span
												className={cn(
													'text-base font-semibold leading-7 text-black',
													isSelected ? 'text-red' : 'text-black',
													isBlockedOption(plan.name) &&
														'text-grey-500'
												)}
											>
												{getPlanCheckoutName(plan.name, t, i18n)}
											</span>
											<span
												className={cn(
													'text-xs font-semibold leading-4 text-grey-500 md:leading-5',
													isSelected
														? 'text-red'
														: 'text-grey-500',
													isBlockedOption(plan.name) &&
														'text-grey-300'
												)}
											>
												{t(`plans.${plan.name}.periodicity`)}
											</span>
										</div>
										<span
											className={cn(
												'font-title text-base leading-7 lg:text-[32px] lg:leading-10',
												isSelected ? 'text-red' : 'text-black',
												isBlockedOption(plan.name) &&
													'text-grey-500'
											)}
										>
											{formatPrice({
												amount: plan.baseAmount,
												currency: plan.currency
											})}
										</span>
									</div>
								</div>
							</div>
						);
					})}
				</RadioGroup>
			</div>
			<DialogFooter>
				<Button variant="outline" onClick={() => onOpenChange(false)}>
					{t('common.cancel')}
				</Button>
				<Button
					onClick={() => {
						return onChangePlan ? onChangePlan() : changePlan();
					}}
					disabled={!selectedPlan}
					loading={changingPlan}
				>
					{t(confirmButtonText)}
				</Button>
			</DialogFooter>
		</>
	);
}

export default SelectPlanModal;
